<template>
  <section class="dash-panel">
    <header class="dash-panel-head">
      <div>
        <span class="dash-eyebrow">Tendance</span>
        <h3 class="dash-panel-title">12 derniers mois</h3>
        <p class="dash-panel-sub">Colis livrés, en cours et retournés par mois</p>
      </div>
      <div class="lm-actions">
        <button
          v-if="auth.can('EXPORT_DASH')"
          class="btn btn-sm btn-outline-success"
          type="button"
          @click="exportData"
        >
          <i class="ph ph-file-xls" aria-hidden="true"></i>
          Excel
        </button>
        <button
          class="ui-icon-btn lm-refresh"
          :class="{ spinning: isLoader }"
          type="button"
          aria-label="Actualiser"
          title="Actualiser"
          :disabled="isLoader"
          @click="searchData"
        >
          <i class="ph ph-arrows-clockwise" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <AppLoader v-if="isLoader && !show" label="Chargement des 12 derniers mois…" />
    <div v-else-if="show" class="chart" :class="{ 'is-refreshing': isLoader }">
      <apexchart type="bar" height="340" :options="chartOptions" :series="series" />
    </div>
    <div v-else class="dash-empty">
      <i class="ph ph-chart-bar" aria-hidden="true"></i>
      <p>Statistiques indisponibles pour le moment.</p>
    </div>
  </section>
</template>

<script lang="ts">
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { dashboardGet } from "@/utils/dashboardApi";
import { defineComponent, onMounted, ref } from "vue";

export default defineComponent({
  name: "LastMonthsChart",
  setup() {
    const auth = useAuthStore();
    const isLoader = ref(false);
    const show = ref(false);
    const series = ref([
      { name: "Livrés", data: [] },
      { name: "En Cours", data: [] },
      { name: "Retournés", data: [] },
    ]);

    const muted = "#78716C";
    const grid = getComputedStyle(document.body).getPropertyValue("--ui-border").trim() || "#FADBC8";
    const chartOptions = ref<any>({
      chart: {
        type: "bar",
        height: 340,
        fontFamily: "Inter, sans-serif",
        toolbar: { show: false },
        animations: { enabled: true, speed: 600 },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "58%",
          borderRadius: 5,
          borderRadiusApplication: "end",
        },
      },
      dataLabels: { enabled: false },
      colors: ["#1D9A67", "#3B82F6", "#E44A4A"],
      stroke: { show: true, width: 2, colors: ["transparent"] },
      grid: { borderColor: grid, strokeDashArray: 4, yaxis: { lines: { show: true } } },
      legend: {
        position: "top",
        horizontalAlign: "right",
        fontWeight: 700,
        labels: { colors: muted },
        markers: { size: 6, shape: "circle" },
      },
      xaxis: {
        categories: [],
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: muted, fontWeight: 600 } },
      },
      yaxis: {
        labels: {
          style: { colors: muted },
          formatter: (val: number) => Math.round(val).toLocaleString("fr-FR"),
        },
      },
      fill: { opacity: 1 },
      tooltip: {
        y: { formatter: (val: number) => `${val.toLocaleString("fr-FR")} colis` },
      },
    });

    const exportData = async () => {
      try {
        const res = await api.get("/api/auth/ExcelTopData?type=LastMonth", {
          responseType: "arraybuffer",
        });
        const fileURL = window.URL.createObjectURL(
          new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.download = "StatisticYear.xlsx";
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(fileURL);
      } catch (error) {
        console.error("Export failed:", error);
      }
    };

    const searchData = async () => {
      isLoader.value = true;
      try {
        const data = await dashboardGet("/api/auth/Last12MonthData");
        series.value = data.data;
        // Replace chartOptions so ApexCharts detects the change
        chartOptions.value = {
          ...chartOptions.value,
          colors: data.colors ?? chartOptions.value.colors,
          xaxis: { ...chartOptions.value.xaxis, categories: data.months },
        };
        show.value = true;
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        isLoader.value = false;
      }
    };

    // Loaded on open (it used to wait for a click on "Chercher")
    onMounted(searchData);

    return { isLoader, show, series, chartOptions, searchData, exportData, auth };
  },
});
</script>

<style scoped>
.lm-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.lm-refresh.spinning i {
  animation: lm-spin 0.9s linear infinite;
}
.lm-refresh:disabled {
  cursor: progress;
}
@keyframes lm-spin {
  to { transform: rotate(360deg); }
}
.chart {
  transition: opacity var(--ui-dur) var(--ui-ease);
}
.chart.is-refreshing {
  opacity: 0.45;
  pointer-events: none;
}
</style>
